import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccountBalance as AccountBalanceIcon, Download as DownloadIcon } from '@mui/icons-material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate, STATUS_META } from '../../services/expenseVouchersApi';
import { formatMoney, PAYMENT_STATUS_META, registerApi } from '../../services/expenseAccountsApi';
import {
  PaymentStatusChip,
  useAccountsBase,
  useApprovalsBase,
  VoucherStatusChip,
} from '../../components/expense/voucherShared';
import {
  AccountsExpensesTabs,
  DateRangeFilter,
  EmployeeFilter,
  PaymentTypeFilter,
  useDebounced,
  CompactFilterBar,
} from '../../components/expense/AccountsFilters';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'PENDING_ACCOUNTS,PENDING_APPROVAL', label: 'In approval (open)' },
  ...Object.entries(STATUS_META).filter(([value]) => value !== 'DRAFT').map(([value, m]) => ({ value, label: m.label })),
];

const PAYMENT_STATUS_FILTERS = [
  { value: '', label: 'All' },
  ...Object.entries(PAYMENT_STATUS_META).map(([value, m]) => ({ value, label: m.label })),
];

const EMPTY_FILTERS = {
  status: '',
  paymentStatus: '',
  currency: '',
  employmentCountryId: '',
  expenseCountryId: '',
  jobNameId: '',
  employeeId: '',
  paymentType: '',
  submittedFrom: '',
  submittedTo: '',
  approvedFrom: '',
  approvedTo: '',
};

// [label, sort key or null]
const COLUMNS = [
  ['Voucher No.', 'voucherNumber'],
  ['Voucher Date', 'voucherDate'],
  ['Submitted', 'submittedAt'],
  ['Approved', 'approvedAt'],
  ['Employee', 'employee'],
  ['Vendor', null],
  ['Head', null],
  ['Payment Type', null],
  ['Requested', null],
  ['Net Payable', null],
  ['Paid', null],
  ['Balance', null],
  ['Status', 'status'],
  ['Payment Status', 'paymentStatus'],
  ['Project', null],
];

const FILL = { width: '100%', minWidth: 0 };

// Accounts "All Vouchers" register: every submitted voucher (drafts excluded), read-only.
// Filtering, search, sorting and pagination all happen on the server. Clicking a voucher
// opens the existing screen for it (Accounts detail when approved, approval review otherwise).
const AccountsVoucherRegister = () => {
  const navigate = useNavigate();
  const accountsBase = useAccountsBase();
  const approvalsBase = useApprovalsBase();
  const { countries, currencies, jobNames, users } = useExpenseLookups(['countries', 'currencies', 'jobNames', 'users']);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [vendor, setVendor] = useState('');
  const debouncedSearch = useDebounced(search.trim());
  const debouncedVendor = useDebounced(vendor.trim());
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ sort: 'voucherDate', order: 'desc' });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(0); }, [debouncedSearch, debouncedVendor, filters, sort]);

  const params = { search: debouncedSearch, vendor: debouncedVendor, ...filters, ...sort };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await registerApi.list({ ...params, page: page + 1, limit: rowsPerPage });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
      setError(null);
    } catch (e) {
      setRows([]);
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedVendor, filters, sort, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const setFilterValue = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const toggleSort = (key) => setSort((s) => ({ sort: key, order: s.sort === key && s.order === 'desc' ? 'asc' : 'desc' }));
  const clearFilters = () => {
    setSearch('');
    setVendor('');
    setFilters(EMPTY_FILTERS);
  };

  const exportLines = async () => {
    setExporting(true);
    try {
      await registerApi.exportLines(params);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const open = (row) => {
    if (row.detailView === 'ACCOUNTS') navigate(`${accountsBase}/${row._id}`);
    else if (row.detailView === 'APPROVAL') navigate(`${approvalsBase}/${row._id}`);
  };

  const money = (row, field) => (typeof row[field] === 'number' ? formatMoney(row[field], row.currency) : '-');
  const countryOptions = countries.map((c) => <MenuItem key={c.value} value={c.value}>{c.raw?.name || c.label}</MenuItem>);

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color="primary" />
            Accounts Expenses
          </Typography>
          <Typography variant="body1" color="text.secondary">All vouchers of every employee, submitted for approval (read-only). Drafts stay private to the employee until submitted.</Typography>
        </Box>
        <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={exportLines} disabled={exporting} sx={{ textTransform: 'none' }}>
          Export lines (CSV)
        </Button>
      </Box>

      <AccountsExpensesTabs />

      <CompactFilterBar data-testid="register-filters">
        <TextField size="small" placeholder="Search voucher, invoice, employee, vendor..." value={search} onChange={(e) => setSearch(e.target.value)} sx={FILL} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} sx={FILL} />
        <TextField size="small" label="Vendor" placeholder="Vendor name" value={vendor} onChange={(e) => setVendor(e.target.value)} sx={FILL} />
        <TextField select size="small" label="Status" value={filters.status} onChange={setFilter('status')} sx={FILL}>
          {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Payment status" value={filters.paymentStatus} onChange={setFilter('paymentStatus')} sx={FILL}>
          {PAYMENT_STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={FILL}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <PaymentTypeFilter value={filters.paymentType} onChange={setFilterValue('paymentType')} sx={FILL} />
        <TextField select size="small" label="Employment country" value={filters.employmentCountryId} onChange={setFilter('employmentCountryId')} sx={FILL}>
          <MenuItem value="">All</MenuItem>
          {countryOptions}
        </TextField>
        <TextField select size="small" label="Expense country" value={filters.expenseCountryId} onChange={setFilter('expenseCountryId')} sx={FILL}>
          <MenuItem value="">All</MenuItem>
          {countryOptions}
        </TextField>
        <TextField select size="small" label="Project" value={filters.jobNameId} onChange={setFilter('jobNameId')} sx={FILL}>
          <MenuItem value="">All</MenuItem>
          {jobNames.map((j) => <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Submitted" from={filters.submittedFrom} to={filters.submittedTo}
          onFromChange={setFilterValue('submittedFrom')} onToChange={setFilterValue('submittedTo')} sx={FILL} />
        <DateRangeFilter label="Approved" from={filters.approvedFrom} to={filters.approvedTo}
          onFromChange={setFilterValue('approvedFrom')} onToChange={setFilterValue('approvedTo')} sx={FILL} />
        <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', justifySelf: 'start' }}>Clear filters</Button>
      </CompactFilterBar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load vouchers: {error.message}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {COLUMNS.map(([label, key]) => (
                <TableCell key={label} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {key ? (
                    <TableSortLabel
                      active={sort.sort === key}
                      direction={sort.sort === key ? sort.order : 'desc'}
                      onClick={() => toggleSort(key)}
                      sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLUMNS.length} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">No vouchers match.</Typography></TableCell></TableRow>
            ) : rows.map((row) => {
              const clickable = Boolean(row.detailView);
              const tr = (
                <TableRow key={row._id} hover={clickable} sx={{ cursor: clickable ? 'pointer' : 'default' }} onClick={() => open(row)}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {row.voucherNumber}
                    {row.isSettlement && <Chip size="small" label="Settlement" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.voucherDate)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.submittedAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.approvedAt)}</TableCell>
                  <TableCell>{row.employeeName}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.department}</Typography></TableCell>
                  <TableCell>{row.vendors.join(', ') || '-'}</TableCell>
                  <TableCell>{row.heads.join(', ') || '-'}</TableCell>
                  <TableCell>{row.paymentTypes.join(', ') || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(row, 'requestedAmount')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{money(row, 'netPayable')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.netPayable === null ? '-' : money(row, 'paidTotal')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(row, 'balancePayable')}</TableCell>
                  <TableCell><VoucherStatusChip status={row.status} /></TableCell>
                  <TableCell>{row.paymentStatus ? <PaymentStatusChip status={row.paymentStatus} /> : '-'}</TableCell>
                  <TableCell>{row.jobName || '-'}</TableCell>
                </TableRow>
              );
              return clickable ? tr : (
                <Tooltip key={row._id} title="Draft - not submitted yet, only the employee can open it" followCursor>{tr}</Tooltip>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default AccountsVoucherRegister;
